import type { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import { splitDiagnosisCodeAndDescription } from './treatmentPlanFormHelpers';

export const TREATMENT_PLAN_DIAGNOSIS_LINK_ID_SUFFIX = '-diagnosis-code';

export interface ParsedTreatmentPlanDiagnosis {
  code: string;
  display: string;
  raw: string;
}

function firstAnswerString(item: QuestionnaireResponseItem | undefined): string | undefined {
  const value = item?.answer?.[0]?.valueString?.trim();
  return value || undefined;
}

/** Collect all non-empty answer strings for items whose linkId ends with the given suffix. */
export function collectAnswerStringsByLinkIdSuffix(
  items: QuestionnaireResponseItem[] | undefined,
  suffix: string,
): string[] {
  const results: string[] = [];
  const walk = (nodes: QuestionnaireResponseItem[] | undefined): void => {
    for (const node of nodes ?? []) {
      if (node.linkId?.endsWith(suffix)) {
        const text = firstAnswerString(node);
        if (text) results.push(text);
      }
      walk(node.item);
    }
  };
  walk(items);
  return results;
}

/** Parse ICD-10 code + display from treatment-plan QR diagnosis answers. */
export function extractDiagnosesFromQuestionnaireResponse(
  qr: Pick<QuestionnaireResponse, 'item'>,
): ParsedTreatmentPlanDiagnosis[] {
  const rawValues = collectAnswerStringsByLinkIdSuffix(qr.item, 'diagnosis-code');
  const parsed: ParsedTreatmentPlanDiagnosis[] = [];

  for (const raw of rawValues) {
    const { code, description } = splitDiagnosisCodeAndDescription(raw);
    if (!code) continue;
    parsed.push({
      code,
      display: description || code,
      raw,
    });
  }

  return parsed;
}
